import { useProductRelations } from 'components/products/relations';
import { LoadingSpinner } from 'components/ui';
import { isEqual, omit, orderBy } from 'lodash';
import querystring from 'querystring';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useVirtual } from 'react-virtual';
import { useAppSelector } from 'store';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';
import { ProductImage } from '../ProductImage';

export const ProductSelector = () => {
  const history = useHistory();

  const [search, setSearch] = useState('');
  const {
    selectedProduct,
    setSelectedProduct,
    productListIsLoading,
    productList,
  } = useProductRelations();

  const { rawQueryParams } = useParseURLQueryParams();
  const { product_id, start_date, end_date } = rawQueryParams;

  const { currentStore } = useAppSelector(userDetailsState);

  const currency = currentStore.completeInfo?.currency || 'USD';
  const locale = currentStore.completeInfo?.primary_locale || 'en';

  function clearUrlSelectedProduct() {
    history.push({
      search: querystring.stringify(
        omit(
          {
            ...rawQueryParams,
          },
          ['product_id']
        )
      ),
    });
  }

  // Clear selection on change date
  useEffect(() => {
    if (selectedProduct && start_date && end_date) {
      clearUrlSelectedProduct();
      setSelectedProduct(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end_date, start_date]);

  // If URL has product id, set it as selected Product
  useEffect(() => {
    if (
      history.location &&
      productList?.length &&
      product_id &&
      !!!selectedProduct
    ) {
      const current = productList?.find(
        (item) => String(item.id) === product_id
      );
      setSelectedProduct(current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location, productList]);

  const sortedData = orderBy(
    productList,
    (item) => item.unique_customers,
    'desc'
  );

  const filteredData = search
    ? sortedData.filter((item) =>
        item.title.toLowerCase().includes(search.toLowerCase())
      )
    : sortedData;

  const parentRef = useRef<any>();
  const rowVirtualizer = useVirtual({
    size: filteredData?.length,
    parentRef,
    estimateSize: useCallback(() => 142, []),
  });

  const handleSelectProduct = (index: number) => {
    const selected = filteredData[index];

    clearUrlSelectedProduct();

    if (isEqual(selected, selectedProduct)) {
      setSelectedProduct(undefined);

      return;
    }

    setSelectedProduct(selected);
    history.push({
      search: querystring.stringify({
        ...rawQueryParams,
        product_id: selected?.id,
      }),
    });
  };

  if (productListIsLoading) {
    return (
      <div className="grid h-full place-items-center">
        <LoadingSpinner className="w-[64px] h-[64px] text-segments-accent-color-orange" />
      </div>
    );
  }

  return (
    <div className="relative h-full pl-5 pr-1.5">
      <div className="pt-5 pr-3.5">
        <input
          type="text"
          className="w-full rounded-md border-segments-gray "
          placeholder="search product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {((!!!filteredData?.length && productList?.length) ||
        (!!!productListIsLoading && !!!productList?.length)) && (
        <div className="absolute inset-0 grid h-full place-items-center">
          <p>No product found</p>
        </div>
      )}

      <div
        ref={parentRef}
        className="pr-2 my-2 List scrollbar-thin scrollbar-thumb-segments-darker-gray-75 scrollbar-thumb-rounded-md scrollable-parent"
        style={{
          height: `820px`,
          width: `100%`,
          overflow: 'auto',
        }}
      >
        <div
          className="truncate divide-y ListInner"
          style={{
            height: `${rowVirtualizer.totalSize}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) => {
            return (
              <div
                key={virtualRow.index}
                className="py-2 group virtualized-child"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <button
                  className={`relative w-full h-full flex items-start space-x-4 p-4 rounded-md group-hover:bg-segments-gray-F8 group-hover:opacity-100 ${
                    selectedProduct?.id === filteredData[virtualRow.index].id
                      ? 'bg-segments-gray-F8 opacity-100 border-2 border-segments-accent-color-1'
                      : 'opacity-50'
                  } `}
                  onClick={() => handleSelectProduct(virtualRow.index)}
                >
                  <ProductImage
                    productId={filteredData[virtualRow.index].id}
                    className="object-contain w-16 h-16 bg-white rounded-lg"
                    height="64"
                    width="64"
                  />
                  <div className="space-y-2 text-left">
                    <p
                      className={`font-bold leading-4 whitespace-pre-line text-sm line-clamp-3 ${
                        selectedProduct?.id ===
                          filteredData[virtualRow.index].id &&
                        'text-segments-accent-color-1'
                      }`}
                    >
                      {filteredData[virtualRow.index].title}
                    </p>
                    <div>
                      <p className="leading-4 text-black text-opacity-50 text-segments-13px">
                        Customers:{' '}
                        {filteredData[
                          virtualRow.index
                        ].unique_customers.toLocaleString()}{' '}
                      </p>
                      <p className="leading-4 text-black text-opacity-50 text-segments-13px">
                        Avg. CLV:{' '}
                        {filteredData[virtualRow.index].aclv?.toLocaleString(
                          locale,
                          {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </p>
                    </div>
                    <div className="absolute inset-0 items-center justify-end hidden p-4 text-right group-hover:flex">
                      <svg
                        width="28"
                        height="28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="14" cy="14" r="14" fill="#125970" />
                        <path
                          d="M12 20l5.625-5.625L12 8.75"
                          stroke="#fff"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
